/**
 * API Key React Query Hooks
 *
 * Provides hooks for listing, creating, and revoking API keys via the
 * `manage-api-keys` Supabase Edge Function.
 *
 * Authentication uses the user's JWT session token (not an API key).
 * The full key value is only returned once on creation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { reportError } from '@/lib/errors';

// ============================================================================
// Types
// ============================================================================

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface CreateApiKeyResponse extends ApiKey {
  /** Full key value — shown only once at creation time. Store it immediately. */
  key: string;
}

export interface CreateApiKeyInput {
  name: string;
  scopes?: string[];
  expires_at?: string | null;
}

// ============================================================================
// Constants
// ============================================================================

const QUERY_KEY = ['api-keys'] as const;

const EDGE_FN_URL = `${process.env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/manage-api-keys`;

// ============================================================================
// Internal helper — get auth headers from the current session
// ============================================================================

async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('No active session. Please sign in again.');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`,
  };
}

// ============================================================================
// useApiKeys — list active API keys (GET)
// ============================================================================

export function useApiKeys() {
  return useQuery<ApiKey[]>({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<ApiKey[]> => {
      const headers = await getAuthHeaders();

      const response = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? `Failed to fetch API keys (${response.status})`);
      }

      const data = await response.json();
      return (data.keys ?? []) as ApiKey[];
    },
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// useCreateApiKey — create a new API key (POST)
// ============================================================================

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation<CreateApiKeyResponse, Error, CreateApiKeyInput>({
    mutationFn: async (input: CreateApiKeyInput): Promise<CreateApiKeyResponse> => {
      const headers = await getAuthHeaders();

      const response = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? `Failed to create API key (${response.status})`);
      }

      const data = await response.json();
      return data as CreateApiKeyResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      reportError(error, { context: 'useCreateApiKey' });
    },
  });
}

// ============================================================================
// useRevokeApiKey — revoke (delete) an API key (DELETE)
// ============================================================================

export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (keyId: string): Promise<void> => {
      const headers = await getAuthHeaders();

      const response = await fetch(EDGE_FN_URL, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ keyId }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? `Failed to revoke API key (${response.status})`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => {
      reportError(error, { context: 'useRevokeApiKey' });
    },
  });
}
